import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { ComponentProps, useMemo } from "react";

import { useCalendarContext } from "../../../context/calendar_context";
import { useCalendarHeader } from "../use_calendar_header";
import { MonthItem } from "./month_item";
import css from "./months.module.scss";

type MonthsProps = {
  startDate: string;
  endDate: string;
};

export const Months = ({ startDate, endDate }: MonthsProps): JSX.Element => {
  const { monthsHeight } = useCalendarHeader();
  const { dayWidth } = useCalendarContext();
  const { utc, formatISODate } = useDateFormatter();

  const monthItems = useMemo(() => {
    const firstFullMonthDate = utc(startDate).endOf("month").add(1, "month");
    const lastMonthDaysOffset =
      utc(endDate).startOf("month").subtract(1, "day").diff(utc(startDate), "days") + 1;
    const lastFullMonthDate = utc(endDate).endOf("month").subtract(1, "month");
    const fullMonthsCount = lastFullMonthDate.diff(firstFullMonthDate, "months") + 1;

    const firsMonthDaysCount = utc(startDate).endOf("month").diff(utc(startDate), "days") + 1;
    const lastMonthDaysCount = utc(endDate).diff(utc(endDate).startOf("month"), "days") + 1;
    const fullMonthsProps = [...Array(fullMonthsCount)].map((_, monthNumber) => {
      const currentMonth = firstFullMonthDate.clone().add(monthNumber, "months");

      return {
        monthDate: formatISODate(currentMonth),
        daysOffset: currentMonth.clone().startOf("month").subtract(1, "day").diff(utc(startDate), "days") + 1
      };
    });
    const allMonthsProps: Omit<ComponentProps<typeof MonthItem>, "dayWidth">[] = [
      {
        monthDate: startDate,
        daysOffset: 0,
        daysCount: firsMonthDaysCount
      },
      ...fullMonthsProps,
      {
        monthDate: endDate,
        daysOffset: lastMonthDaysOffset,
        daysCount: lastMonthDaysCount
      }
    ];
    return allMonthsProps.map(({ monthDate, daysOffset, daysCount }) => (
      <MonthItem
        key={`month_${monthDate}`}
        daysOffset={daysOffset}
        monthDate={monthDate}
        daysCount={daysCount}
        dayWidth={dayWidth}
      />
    ));
  }, [startDate, endDate, dayWidth]);

  return (
    <div style={{ height: monthsHeight }} className={css.months}>
      {monthItems}
    </div>
  );
};
